import { useEffect, useState } from "react";
import { Link, useLocation } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import { Menu, X, MessageCircle, Sparkles } from "lucide-react";
import { NAV_LINKS, BUSINESS, waLink } from "@/data/site";

const AnnouncementBar = () => (
  <div
    data-testid="announcement-bar"
    className="w-full bg-primary text-primary-foreground text-xs sm:text-sm font-medium text-center py-2 px-4"
  >
    <span className="inline-flex items-center gap-2">
      <Sparkles className="w-3.5 h-3.5" />
      Booking projects for Q3 · Serving {BUSINESS.serviceArea} · Free website mockup on request
    </span>
  </div>
);

const Logo = () => (
  <Link to="/" data-testid="nav-logo" className="flex items-center gap-2.5 group">
    <span className="grid place-items-center w-9 h-9 rounded-xl bg-primary text-primary-foreground font-heading font-extrabold text-lg shadow-[0_0_24px_-6px_rgba(229,37,37,0.6)] transition-transform group-hover:scale-105">
      R
    </span>
    <span className="font-heading font-bold text-lg tracking-tight">
      RedKey<span className="text-primary">.</span>
    </span>
  </Link>
);

export const Navbar = () => {
  const [scrolled, setScrolled] = useState(false);
  const [open, setOpen] = useState(false);
  const { pathname } = useLocation();

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 20);
    onScroll();
    window.addEventListener("scroll", onScroll);
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  useEffect(() => setOpen(false), [pathname]);

  return (
    <>
      <header className="fixed top-0 inset-x-0 z-50">
        <AnnouncementBar />
        <nav
          className={`transition-colors duration-300 ${scrolled ? "glass border-b border-border" : "bg-transparent"}`}
        >
          <div className="max-w-7xl mx-auto px-5 sm:px-8 h-16 flex items-center justify-between">
            <Logo />
            <div className="hidden lg:flex items-center gap-1">
              {NAV_LINKS.map((l) => (
                <Link
                  key={l.to}
                  to={l.to}
                  data-testid={`nav-link-${l.label.toLowerCase()}`}
                  className={`relative px-4 py-2 text-sm font-medium rounded-full transition-colors ${
                    pathname === l.to ? "text-foreground" : "text-muted-foreground hover:text-foreground"
                  }`}
                >
                  {l.label}
                  {pathname === l.to && (
                    <motion.span
                      layoutId="nav-dot"
                      className="absolute left-1/2 -translate-x-1/2 bottom-1 w-1 h-1 rounded-full bg-primary"
                    />
                  )}
                </Link>
              ))}
            </div>
            <div className="flex items-center gap-2">
              <a
                href={waLink(`Hi ${BUSINESS.name}, I'd like a free website mockup.`)}
                target="_blank"
                rel="noopener noreferrer"
                data-testid="nav-whatsapp-btn"
                className="hidden sm:inline-flex items-center gap-2 rounded-full bg-primary hover:bg-[#c81f1f] text-primary-foreground text-sm font-semibold px-5 py-2.5 transition-colors"
              >
                <MessageCircle className="w-4 h-4" /> Get Free Mockup
              </a>
              <button
                onClick={() => setOpen((v) => !v)}
                data-testid="mobile-menu-toggle"
                className="lg:hidden grid place-items-center w-10 h-10 rounded-full border border-border text-foreground"
                aria-label="Toggle menu"
              >
                {open ? <X className="w-5 h-5" /> : <Menu className="w-5 h-5" />}
              </button>
            </div>
          </div>
        </nav>
      </header>

      <AnimatePresence>
        {open && (
          <motion.div
            data-testid="mobile-menu"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 z-40 lg:hidden glass pt-28 px-6"
          >
            <div className="flex flex-col gap-1">
              {NAV_LINKS.map((l, i) => (
                <motion.div
                  key={l.to}
                  initial={{ opacity: 0, x: -20 }}
                  animate={{ opacity: 1, x: 0 }}
                  transition={{ delay: 0.05 * i }}
                >
                  <Link
                    to={l.to}
                    data-testid={`mobile-nav-link-${l.label.toLowerCase()}`}
                    className="block py-4 text-3xl font-heading font-bold border-b border-border"
                  >
                    {l.label}
                  </Link>
                </motion.div>
              ))}
              <a
                href={waLink(`Hi ${BUSINESS.name}, I'd like a free website mockup.`)}
                target="_blank"
                rel="noopener noreferrer"
                className="mt-6 inline-flex items-center justify-center gap-2 rounded-full bg-primary text-primary-foreground font-semibold px-6 py-4"
              >
                <MessageCircle className="w-5 h-5" /> Get Free Mockup on WhatsApp
              </a>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </>
  );
};
